import { useCallback, useMemo, useState } from 'react';
import { IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import * as api from '../api';
import PageHeader from '../components/ui/PageHeader';
import DataTable from '../components/ui/DataTable';
import CopyButton from '../components/ui/CopyButton';
import Modal from '../components/ui/Modal';
import ConfirmModal from '../components/ui/ConfirmModal';
import FormField from '../components/ui/FormField';
import ErrorMessage from '../components/ui/ErrorMessage';
import { usePaginatedList } from '../hooks/usePaginatedList';
import './CrudPage.scss';

const SERVER_FIELDS = [
    { key: 'ip', label: 'IP', required: true },
    { key: 'domain', label: 'Домен', required: true },
    { key: 'root_pass', label: 'Root пароль', required: true, secret: true },
    { key: 'xui_url', label: 'XUI URL', required: true },
    { key: 'api_key', label: 'API ключ', required: true, secret: true },
    { key: 'sub_port', label: 'Sub порт', type: 'number', required: true },
    { key: 'seats', label: 'Мест', type: 'number', required: true },
    { key: 'seats_occupied', label: 'Занято мест', type: 'number' },
    { key: 'key_path', label: 'Путь к ключу', required: true },
    { key: 'cert_path', label: 'Путь к сертификату', required: true },
];

function SecretCell({ value }) {
    if (!value) return '—';
    return (
        <div className="cell-secret">
            <span title={value}>{value}</span>
            <CopyButton value={value} />
        </div>
    );
}

function emptyForm() {
    return {
        ip: '',
        domain: '',
        root_pass: '',
        xui_url: '',
        api_key: '',
        sub_port: '443',
        seats: '100',
        seats_occupied: '0',
        key_path: '',
        cert_path: '',
    };
}

export default function ServersPage() {
    const fetchList = useCallback(
        (params) => api.listResource('servers', params),
        [],
    );
    const {
        items, total, offset, limit, loading, error, setOffset, reload,
    } = usePaginatedList(fetchList);

    const [modalOpen, setModalOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm());
    const [formError, setFormError] = useState('');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const openCreate = () => {
        setEditing(null);
        setForm(emptyForm());
        setFormError('');
        setModalOpen(true);
    };

    const openEdit = (row) => {
        setEditing(row);
        setForm(Object.fromEntries(
            SERVER_FIELDS.map(({ key }) => [key, row[key] != null ? String(row[key]) : '']),
        ));
        setFormError('');
        setModalOpen(true);
    };

    const handleField = (key) => (e) => {
        setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };

    const handleSave = async () => {
        setSaving(true);
        setFormError('');
        try {
            const body = {};
            SERVER_FIELDS.forEach(({ key, type }) => {
                const raw = form[key];
                if (raw === '' && editing && key === 'seats_occupied') return;
                body[key] = type === 'number' ? Number(raw) : raw;
            });

            if (editing) {
                const patch = {};
                SERVER_FIELDS.forEach(({ key, type }) => {
                    const next = type === 'number' ? Number(form[key]) : form[key];
                    if (next !== editing[key]) patch[key] = next;
                });
                await api.updateResource('servers', editing.id, patch);
            } else {
                await api.createResource('servers', body);
            }
            setModalOpen(false);
            reload();
        } catch (err) {
            setFormError(err.message || 'Ошибка сохранения');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await api.deleteResource('servers', deleteTarget.id);
            setDeleteTarget(null);
            reload();
        } catch (err) {
            setFormError(err.message || 'Ошибка удаления');
        } finally {
            setDeleting(false);
        }
    };

    const columns = useMemo(() => [
        { key: 'id', title: 'ID', width: 60 },
        { key: 'ip', title: 'IP' },
        { key: 'domain', title: 'Домен' },
        {
            key: 'seats',
            title: 'Места',
            render: (_, row) => `${row.seats_occupied ?? 0} / ${row.seats ?? 0}`,
        },
        {
            key: 'root_pass',
            title: 'Root',
            render: (v) => <SecretCell value={v} />,
        },
        {
            key: 'api_key',
            title: 'API ключ',
            render: (v) => <SecretCell value={v} />,
        },
        {
            key: 'actions',
            title: '',
            render: (_, row) => (
                <div className="cell-actions">
                    <button type="button" className="btn btn--outline btn--sm" onClick={() => openEdit(row)}>
                        <IconEdit size={16} />
                    </button>
                    <button type="button" className="btn btn--danger btn--sm" onClick={() => setDeleteTarget(row)}>
                        <IconTrash size={16} />
                    </button>
                </div>
            ),
        },
    ], []);

    return (
        <div className="crud-page">
            <PageHeader
                title="Серверы"
                subtitle="Управление VPN-серверами"
                actions={(
                    <button type="button" className="btn btn--primary btn--sm" onClick={openCreate}>
                        <IconPlus size={16} />
                        Добавить
                    </button>
                )}
            />

            <ErrorMessage message={error} onRetry={reload} />

            <DataTable
                columns={columns}
                rows={items}
                loading={loading}
                pagination={{ total, limit, offset, onPageChange: setOffset }}
            />

            <Modal
                open={modalOpen}
                title={editing ? `Сервер #${editing.id}` : 'Новый сервер'}
                onClose={() => setModalOpen(false)}
                wide
                footer={(
                    <>
                        <button type="button" className="btn btn--outline btn--sm" onClick={() => setModalOpen(false)}>
                            Отмена
                        </button>
                        <button type="button" className="btn btn--primary btn--sm" onClick={handleSave} disabled={saving}>
                            {saving ? 'Сохранение...' : 'Сохранить'}
                        </button>
                    </>
                )}
            >
                <ErrorMessage message={formError} />
                <div className="form-grid">
                    {SERVER_FIELDS.map(({ key, label, type = 'text' }) => (
                        <FormField
                            key={key}
                            label={label}
                            type={type === 'number' ? 'number' : 'text'}
                            value={form[key]}
                            onChange={handleField(key)}
                        />
                    ))}
                </div>
            </Modal>

            <ConfirmModal
                open={Boolean(deleteTarget)}
                title="Удалить сервер?"
                message={`Сервер ${deleteTarget?.ip || ''} будет удалён без возможности восстановления.`}
                confirmLabel="Удалить"
                danger
                loading={deleting}
                onConfirm={handleDelete}
                onClose={() => setDeleteTarget(null)}
            />
        </div>
    );
}
